/**
 * CR-6244: the tenant's button colour in light and dark mode.
 *
 * The button colour becomes the theme's `--primary` — buttons, links, active
 * tabs and the sidebar's current page. It used to be written inline on
 * <html>, which beats the `.dark` tokens, so dark mode kept a dark brand
 * colour: dark button text on it, and brand-coloured links, on a near-black
 * page, all failed contrast. Light mode is unchanged. Dark mode lightens the
 * colour, keeping its hue and saturation, until it reads on dark surfaces and
 * carries the theme's dark button text, as the stock dark theme does
 * with its own blue.
 */

/**
 * WCAG AA for normal-size text is 4.5:1. Aim a little above it, so the
 * browser's own rounding when it converts the HSL value cannot land under.
 */
const AA_TARGET = 4.6;
/**
 * Dark mode's `--muted` and `--primary-foreground` (index.css `.dark`). The
 * muted surface is the lightest the colour sits on (tabs, hovers, tinted
 * panels); readable there, it is readable on the darker page too.
 */
const DARK_SURFACE = '#1e293b';
const DARK_PRIMARY_TEXT = '#0f172a';

type RGB = [number, number, number];

/** "#abc" or "#aabbcc" as "#aabbcc"; null for anything that is not a colour. */
function normaliseHex(hex: string): string | null {
  const h = hex.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(h)) return `#${h.split('').map((c) => c + c).join('')}`;
  return /^[0-9a-f]{6}$/i.test(h) ? `#${h}` : null;
}

function hexToRgb(hex: string): RGB {
  const h = hex.replace(/^#/, '');
  return [0, 2, 4].map((i) => parseInt(h.substring(i, i + 2), 16)) as RGB;
}

function rgbToHsl([r, g, b]: RGB): [number, number, number] {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return [h * 60, s * 100, l * 100];
}

function hslToRgb(h: number, s: number, l: number): RGB {
  const sn = s / 100;
  const ln = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n: number) => ln - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0), f(8), f(4)].map((v) => Math.round(v * 255)) as RGB;
}

function luminance([r, g, b]: RGB): number {
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/**
 * WCAG contrast ratio between two hex colours.
 *
 * @param a - a hex colour
 * @param b - a hex colour
 * @returns the ratio, 1 to 21
 */
export function contrastRatio(a: string, b: string): number {
  const [la, lb] = [luminance(hexToRgb(a)), luminance(hexToRgb(b))];
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

const toHslToken = (h: number, s: number, l: number) =>
  `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;

const toHex = (rgb: RGB) => `#${rgb.map((v) => v.toString(16).padStart(2, '0')).join('')}`;

/**
 * The button colour as dark mode draws it, as an HSL token ("H S% L%").
 *
 * @example
 * darkModePrimary('#0066cc') // a lighter blue, same hue
 *
 * @param hex - the tenant's button colour
 * @returns the colour, lightened only as far as it needs to be
 */
export function darkModePrimary(hex: string): string {
  // Whole numbers throughout, so the colour checked is the colour written.
  const [h, s, l] = rgbToHsl(hexToRgb(normaliseHex(hex) ?? hex)).map(Math.round);
  for (let light = l; light <= 100; light += 1) {
    const rgb = toHex(hslToRgb(h, s, light));
    if (contrastRatio(rgb, DARK_SURFACE) >= AA_TARGET && contrastRatio(rgb, DARK_PRIMARY_TEXT) >= AA_TARGET) {
      return toHslToken(h, s, light);
    }
  }
  return toHslToken(h, s, 100);
}

const PRIMARY_STYLE_ID = 'branding-primary-colour';

/**
 * Write the tenant's button colour as the theme's primary: as-is in light
 * mode, lightened in dark mode. The stock colour writes nothing, so each
 * theme keeps its own.
 *
 * @param hex - the tenant's button colour, or null for the stock colour; a
 *   value that is not a colour is treated as the stock colour
 */
export function applyPrimaryColour(hex: string | null): void {
  const root = document.documentElement;
  // Earlier builds wrote these inline, where they override the dark tokens.
  root.style.removeProperty('--primary');
  root.style.removeProperty('--ring');

  let rule = document.getElementById(PRIMARY_STYLE_ID);
  const colour = hex ? normaliseHex(hex) : null;
  if (!colour) {
    rule?.remove();
    return;
  }
  if (!rule) {
    rule = document.createElement('style');
    rule.id = PRIMARY_STYLE_ID;
    document.head.appendChild(rule);
  }
  const light = toHslToken(...rgbToHsl(hexToRgb(colour)));
  const dark = darkModePrimary(colour);
  rule.textContent = `:root:not(.dark) { --primary: ${light}; --ring: ${light}; } `
    + `:root.dark { --primary: ${dark}; --ring: ${dark}; }`;
}
